// frontend/src/api/leagueApi.js
// League-related API calls extracted from DatabaseAPI.js
// Phase 1 Platform Extraction — Step 15B

import { API_BASE, ENDPOINTS, getCsrfHeaders } from './transport';
import { handleApiAuthError } from '../auth/authUtils';

export function getInitialData() {
    return fetch(`${API_BASE}/v2/initial/`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        console.log('Initial Data:', data);
        return data;
      })
      .catch(error => console.error('Error fetching initial data:', error));
}

export function getWeekStatus(leagueId) {
    return fetch(`${API_BASE}/v2/leagues/${leagueId}/stats/`, { credentials: 'include' })
        .then(response => response.json())
        .then(data => {
            console.log('Week Status:', data);
            return data;
        })
        .catch(error => {
            console.error('Error fetching week status:', error);
            return null;
        });
}

export function getLeagueStandings(leagueId) {
    return fetch(`${API_BASE}/v2/leagues/${leagueId}/standings/`, { credentials: 'include' })
        .then(response => response.json());
}

export function getGlobalLeaderboard(filterType = 'highscore', timeframe = 'lastweek') {
    const params = new URLSearchParams({
        filter_type: filterType,
        timeframe: timeframe
    });

    return fetch(`${API_BASE}/v2/leaderboard/?${params}`, { credentials: 'include' })
        .then(response => {
            console.log('🔍 DIAGNOSTIC: Leaderboard response status:', response.status);
            console.log('🔍 DIAGNOSTIC: Response headers:', response.headers);

            if (!response.ok) {
                return response.json()
                    .then(errorData => {
                        console.error('🔍 DIAGNOSTIC: Error response body:', errorData);
                        throw new Error(`HTTP ${response.status}: ${errorData.detail || errorData.error || 'Failed to fetch leaderboard'}`);
                    })
                    .catch(jsonError => {
                        console.error('🔍 DIAGNOSTIC: Could not parse error response as JSON:', jsonError);
                        return response.text().then(text => {
                            console.error('🔍 DIAGNOSTIC: Error response text:', text);
                            throw new Error(`HTTP ${response.status}: ${text || 'Failed to fetch leaderboard'}`);
                        });
                    });
            }
            return response.json();
        })
        .then(data => {
            console.log('✅ DatabaseAPI: Global leaderboard retrieved:', data);
            return data;
        })
        .catch(error => {
            console.error('❌ DatabaseAPI: Error getting global leaderboard:', error);
            throw error;
        });
}

export function getHallOfFame() {
    return fetch(`${API_BASE}/v2/hall-of-fame/`, { credentials: 'include' })
        .then(response => {
            console.log('🏆 DatabaseAPI: Hall of Fame response status:', response.status);

            if (!response.ok) {
                return response.json()
                    .then(errorData => {
                        console.error('🏆 DatabaseAPI: Error response body:', errorData);
                        throw new Error(`HTTP ${response.status}: ${errorData.detail || errorData.error || 'Failed to fetch Hall of Fame'}`);
                    })
                    .catch(jsonError => {
                        return response.text().then(text => {
                            console.error('🏆 DatabaseAPI: Error response text:', text);
                            throw new Error(`HTTP ${response.status}: ${text || 'Failed to fetch Hall of Fame'}`);
                        });
                    });
            }
            return response.json();
        })
        .then(data => {
            console.log('✅ DatabaseAPI: Hall of Fame retrieved:', data);
            return data;
        })
        .catch(error => {
            console.error('❌ DatabaseAPI: Error getting Hall of Fame:', error);
            throw error;
        });
}

// GET /api/v2/global-state/ — the server's authoritative current year/week.
//
// MUST GO THROUGH `API_BASE`. A bare `fetch('/api/v2/global-state/')` is
// relative to the PAGE origin, and in dev the page is served by the CRA dev
// server on :3000 while Django listens on :8000 (docker-compose.override.yml
// sets REACT_APP_USE_REVERSE_PROXY=false, so transport.js resolves API_BASE to
// http://<host>:8000/api). There is no `proxy` field in frontend/package.json
// and no setupProxy.js, so the CRA dev server does not forward /api anywhere —
// it answers unknown paths with the SPA's index.html. The caller then gets
// `200 text/html`, and `response.json()` explodes on `<!DOCTYPE html>`. That is
// a client-side origin bug, not an auth failure and not a missing route:
// `/api/v2/global-state/` resolves to GlobalStateView and is the only path that
// does.
//
// `credentials: 'include'` is mandatory: GlobalStateView declares
// SessionAuthentication + IsAuthenticated, so without the session cookie the
// request is anonymous and DRF answers 403.
//
// Errors PROPAGATE. Callers use current_week to select which weekly league's
// data they read, and a defaulted week silently describes a different contest,
// so this function must never substitute a guess for a failed response.
export function getGlobalState() {
    return fetch(`${API_BASE}/v2/global-state/`, { credentials: 'include' })
        .then(response => {
            if (!response.ok) {
                const error = { status: response.status };
                if (handleApiAuthError(error)) {
                    throw new Error('Authentication failed - redirected to login');
                }

                throw new Error(`HTTP ${response.status}: Failed to get global state`);
            }

            // A JSON content type is the proof we reached DRF and not an HTML
            // error/SPA page. Checked BEFORE .json() so the thrown error names
            // the real problem instead of a generic JSON syntax error.
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error(
                    `GET ${API_BASE}/v2/global-state/ returned a non-JSON response ` +
                    `(content-type=${JSON.stringify(contentType)}). The request did not reach ` +
                    'the Django API — check that API_BASE points at the backend origin.'
                );
            }

            return response.json();
        })
        .then(data => {
            // current_year/current_week are SELECTORS for which weekly league is
            // read. A missing or non-integer value must never be defaulted.
            if (!Number.isInteger(data?.current_year) || !Number.isInteger(data?.current_week)) {
                throw new Error(
                    'INVALID GLOBAL STATE from GET /api/v2/global-state/: expected integer ' +
                    `current_year and current_week, got current_year=${JSON.stringify(data?.current_year)} ` +
                    `current_week=${JSON.stringify(data?.current_week)}.`
                );
            }

            return data;
        });
}

export function getWeeklyResults(week = null) {
    let url = `${API_BASE}/v2/weekly-results/`;

    if (week) {
        url += `?week=${week}`;
    }

    return fetch(url, {
        credentials: 'include'
    })
        .then(response => {
            console.log(`DatabaseAPI: Get weekly results response status: ${response.status}`);

            if (!response.ok) {
                const error = { status: response.status };
                if (handleApiAuthError(error)) {
                    throw new Error('Authentication failed - redirected to login');
                }

                throw new Error(`HTTP ${response.status}: Failed to get weekly results`);
            }

            return response.json();
        })
        .then(data => {
            console.log('DatabaseAPI: Weekly results data:', data);
            return data;
        })
        .catch(error => {
            console.error('DatabaseAPI: Error getting weekly results:', error);

            if (error.message.includes('Authentication failed')) {
                throw error;
            }

            return { needs_popup: false, results: [] };
        });
}

export function markWeeklyResultsViewed(leagueId, seasonYear, week) {
    return fetch(`${API_BASE}/v2/weekly-results/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
            league_id: leagueId,
            season_year: seasonYear,
            week: week
        })
    })
        .then(response => {
            console.log(`DatabaseAPI: Mark weekly results viewed response status: ${response.status}`);

            if (!response.ok) {
                const error = { status: response.status };
                if (handleApiAuthError(error)) {
                    throw new Error('Authentication failed - redirected to login');
                }

                throw new Error(`HTTP ${response.status}: Failed to mark results as viewed`);
            }

            return response.json();
        })
        .then(data => {
            console.log('DatabaseAPI: Mark weekly results viewed successful:', data);
            return data;
        })
        .catch(error => {
            console.error('DatabaseAPI: Error marking weekly results as viewed:', error);
            throw error;
        });
}

export function getAvailableLeagues(filters = {}) {
    let url = `${API_BASE}/${ENDPOINTS.LEAGUES.AVAILABLE}/`;

    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.week) params.append('week', filters.week);

    if (params.toString()) {
        url += `?${params.toString()}`;
    }

    return fetch(url, { credentials: 'include' })
        .then(response => {
            console.log(`DatabaseAPI: Get available leagues response status: ${response.status}`);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Failed to get available leagues`);
            }

            return response.json();
        })
        .then(data => {
            console.log('DatabaseAPI: Available leagues data:', data);
            return data;
        })
        .catch(error => {
            console.error('DatabaseAPI: Error getting available leagues:', error);
            return { leagues: [] };
        });
}

export function joinLeague(leagueData) {
    console.log(`🔍 DatabaseAPI: joinLeague called with data:`, leagueData);
    console.log(`🔍 DatabaseAPI: League code: ${leagueData.league_code}`);
    console.log(`🔍 DatabaseAPI: API_BASE: ${API_BASE}`);
    console.log(`🔍 DatabaseAPI: Full URL: ${API_BASE}/${ENDPOINTS.LEAGUES.JOIN}/`);

    return fetch(`${API_BASE}/${ENDPOINTS.LEAGUES.JOIN}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(leagueData)
    })
    .then(response => {
        console.log(`🔍 DatabaseAPI: Join league response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`❌ DatabaseAPI: Join league failed with status ${response.status}:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to join league`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`✅ DatabaseAPI: Join league successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('❌ DatabaseAPI: Error joining league:', error);
        throw error;
    });
}

export function createPrivateLeague(leagueData) {
    console.log(`DatabaseAPI: Creating private league: ${leagueData.league_name}`);

    return fetch(`${API_BASE}/${ENDPOINTS.LEAGUES.CREATE_PRIVATE}/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(leagueData)
    })
    .then(response => {
        console.log(`DatabaseAPI: Create private league response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`DatabaseAPI: Create private league failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to create private league`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`DatabaseAPI: Create private league successful:`, data);
        return data;
    })
    .catch(error => {
        console.error('DatabaseAPI: Error creating private league:', error);
        throw error;
    });
}

export function getPublicLeagueTemplates() {
    console.log(`🔍 DatabaseAPI: getPublicLeagueTemplates called`);

    return fetch(`${API_BASE}/v2/leagues/draft-team/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get public templates response status: ${response.status}`);

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to get public league templates`);
        }

        return response.json();
    })
    .then(data => {
        console.log(`✅ DatabaseAPI: Get public templates response:`, data);

        // VALIDATE EXPECTED DATA FORMAT - NO FALLBACKS
        if (!data.templates || !Array.isArray(data.templates)) {
            throw new Error('❌ INVALID API RESPONSE: Expected "templates" array in response');
        }

        // Validate each template has required fields
        data.templates.forEach((template, index) => {
            const requiredFields = ['template_id', 'template_name', 'template_description', 'display_order', 'is_default'];
            requiredFields.forEach(field => {
                if (template[field] === undefined) {
                    throw new Error(`❌ INVALID TEMPLATE DATA: Template ${index} missing required field "${field}"`);
                }
            });
        });

        return data;
    })
    .catch(error => {
        console.error('❌ DatabaseAPI: Error getting public league templates:', error);
        throw error;
    });
}

// REMOVED: getOpenPublicLeagues()
// GET /api/v2/leagues/open-public/ does not exist on the backend (404). The
// wrapper was deleted rather than left in place, so no caller can reintroduce a
// guaranteed-failing request by accident.
//
// It has no replacement and needs none: the Select Public Arena screen's daily
// path is LIVE off the baseline public-template contract above
// (getPublicLeagueTemplates → GET v2/leagues/draft-team/), which is what develop
// used. Each template carries its own `available_league` when one is open, so the
// arena list does not require a separate open-leagues endpoint.
//
// Do NOT re-add this wrapper (or the `league_mode` validation it performed)
// unless the endpoint itself actually lands on the backend.

export function getPrivateLeagueTemplates() {
    console.log(`🔍 DatabaseAPI: getPrivateLeagueTemplates called`);

    return fetch(`${API_BASE}/v2/leagues/create-private/`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include'
    })
    .then(response => {
        console.log(`DatabaseAPI: Get private templates response status: ${response.status}`);

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to get private league templates`);
        }

        return response.json();
    })
    .then(data => {
        console.log(`✅ DatabaseAPI: Get private templates response:`, data);

        // VALIDATE EXPECTED DATA FORMAT - NO FALLBACKS
        if (!data.templates || !Array.isArray(data.templates)) {
            throw new Error('❌ INVALID API RESPONSE: Expected "templates" array in response');
        }

        // Validate each template has required fields
        data.templates.forEach((template, index) => {
            const requiredFields = ['template_id', 'template_name', 'template_description', 'display_order', 'is_default'];
            requiredFields.forEach(field => {
                if (template[field] === undefined) {
                    throw new Error(`❌ INVALID TEMPLATE DATA: Template ${index} missing required field "${field}"`);
                }
            });
        });

        return data;
    })
    .catch(error => {
        console.error('❌ DatabaseAPI: Error getting private league templates:', error);
        throw error;
    });
}

/**
 * Create a private league.
 *
 * @param {Object} leagueData - { league_name, team_name, known_as, hide_known_as }
 * @param {number} [templateId] - OPTIONAL. Pass ONLY a real, user-selected template
 *        id. Omit it (the normal case today — no selector UI exists) and the backend
 *        resolves the current-week PRIVATE default itself.
 *
 * OMITTED vs NULL IS A REAL DISTINCTION, NOT A STYLE CHOICE.
 * The backend reads `request.data.get('template_id')` and branches on its
 * truthiness (backend/game/api/views/auth_views.py:733,758,781): a truthy id is
 * validated against (id, league_type='PRIVATE', is_active=True) and rejected with
 * 404 if absent, or 400 if it belongs to another year/week; anything falsy takes
 * the `else` branch that resolves `get_default_template_for_week(...)`. So the key
 * must be BUILT OUT of the body rather than set to null/undefined — we never rely
 * on a null happening to be falsy on the far side, and `JSON.stringify` would in
 * any case emit a literal `"template_id": null` for null (only `undefined` is
 * dropped, which is too subtle to depend on).
 *
 * ABSENCE IS LEGAL; BAD INPUT IS NOT. A supplied id is still validated loudly here
 * — a caller that means to pass one must not silently degrade to the default.
 */
export function createPrivateLeagueWithTemplate(leagueData, templateId) {
    const hasTemplateId = templateId !== undefined && templateId !== null;

    if (hasTemplateId) {
        // A SUPPLIED id must be real. Reject junk rather than fall back to the
        // default — a caller passing garbage has a bug we must surface.
        const numericTemplateId = Number(templateId);
        if (!Number.isInteger(numericTemplateId) || numericTemplateId <= 0) {
            throw new Error(
                `❌ INVALID PARAMETER: createPrivateLeagueWithTemplate received template_id "${templateId}" (type ${typeof templateId}) — a supplied template id must be a positive integer from real backend data. Omit the argument entirely to use the backend's current-week PRIVATE default.`
            );
        }
    }

    console.log(
        hasTemplateId
            ? `🔍 DatabaseAPI: createPrivateLeagueWithTemplate called with explicit template_id: ${templateId}`
            : '🔍 DatabaseAPI: createPrivateLeagueWithTemplate called WITHOUT a template_id — backend will resolve the current-week PRIVATE default',
        leagueData
    );

    // Build the body so `template_id` is absent unless we truly have one.
    const requestBody = { ...leagueData };
    if (hasTemplateId) {
        requestBody.template_id = templateId;
    }

    return fetch(`${API_BASE}/v2/leagues/create-private/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getCsrfHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
    })
    .then(response => {
        console.log(`DatabaseAPI: Create private league with template response status: ${response.status}`);

        if (!response.ok) {
            return response.json().then(errorData => {
                console.error(`❌ DatabaseAPI: Create private league with template failed:`, errorData);
                throw new Error(errorData.message || `HTTP ${response.status}: Failed to create private league with template`);
            });
        }

        return response.json();
    })
    .then(data => {
        console.log(`✅ DatabaseAPI: Create private league with template successful:`, data);

        // 🔍 DIAGNOSTIC LOGGING: Check if league_code is present
        console.log('🔍 DatabaseAPI: league_code in response:', data.league_code);
        console.log('🔍 DatabaseAPI: league_code type:', typeof data.league_code);

        // VALIDATE EXPECTED RESPONSE FORMAT - NO FALLBACKS
        const requiredFields = ['status', 'team_id', 'team_name', 'league_id', 'league_name', 'league_code', 'template_name'];
        requiredFields.forEach(field => {
            if (data[field] === undefined) {
                throw new Error(`❌ INVALID RESPONSE: Missing required field "${field}" in create private league response`);
            }
        });

        // 🔍 DIAGNOSTIC: Confirm league_code is now present
        console.log('✅ DatabaseAPI: league_code validation passed:', data.league_code);

        return data;
    })
    .catch(error => {
        console.error('❌ DatabaseAPI: Error creating private league with template:', error);
        throw error;
    });
}
